import PropTypes from "prop-types";
import { useState } from "react";
import { FormattedMessage } from "react-intl";
import { useSelector } from "react-redux";
import CameraAltIcon from "@mui/icons-material/CameraAlt";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { Avatar, Badge, Box, Button, Grid, Tooltip } from "@mui/material";
import { makeStyles, withStyles } from "@mui/styles";
import Banner from "../../../../assets/img/bg/UserDetails.png";
import Profile from "../../../../assets/img/default-avatar.png";
import getGravatarUrl from "../../../../helpers/getGravatarUrl";
import { getCurrentUser } from "../../../../helpers/selectors";
import AvatarUpload from "../AvatarUpload";
import AddressRow from "./AddressRow";
import BioDescription from "./BioDescription";
import EmploymentRow from "./EmploymentRow";
import NameRow from "./NameRow";

const SmallAvatar = withStyles(theme => ({
  root: {
    width: 30,
    height: 30,
    border: `2px solid ${theme.palette.background.paper}`,
  },
}))(Avatar);

const useStyles = makeStyles(theme => ({
  userAvatar: {
    width: 150,
    height: 150,
    borderWidth: 7,
    borderColor: "#fff",
    borderStyle: "solid",
  },
  badgedAvatar: {
    position: "absolute",
    left: 30,
    top: 40,
  },
  btnContainer: {
    marginTop: 40,
  },
  viewAs: {
    position: "absolute",
    top: 0,
    right: 0,
    backgroundColor: "rgba(255, 255, 255, 0.35)",
    border: "1px solid #00000059",
    borderRadius: "4px",
    margin: "18px",
    padding: "12px",
  },
  mainContent: {
    marginTop: 20,
    paddingLeft: 200,
    [theme.breakpoints.down("sm")]: {
      padding: "12px 18px",
      marginTop: 40,
    },
  },
  bioDesc: {
    [theme.breakpoints.down("sm")]: {
      width: "100%",
    },
  },
}));

const CameraAvatar = () => (
  <Tooltip
    arrow
    placement="right"
    title={<FormattedMessage id="avatarUpload.modalTitle" />}
  >
    <SmallAvatar>
      <CameraAltIcon style={{ color: "#000", cursor: "pointer" }} />
    </SmallAvatar>
  </Tooltip>
);

const BannerSection = ({
  accountSettings,
  setAccountSettings,
  setIsViewedAs,
}) => {
  const classes = useStyles();

  const { avatar, username } = useSelector(getCurrentUser) || {};

  const [avatarProfile, setAvatarProfile] = useState(null);
  const [openUpload, setOpenUpload] = useState(false);

  const gravatarUrl = getGravatarUrl(username);

  const uploadAvatar = () => {
    setOpenUpload(!openUpload);
  };

  return accountSettings && (
    <>
      <Box flex={1} style={{ position: "relative" }}>
        <Box flex={1} style={{ height: 130, background: `url(${Banner})`, position: "relative" }}>
          <Badge
            overlap="circle"
            anchorOrigin={{
              vertical: "bottom",
              horizontal: "right",
            }}
            badgeContent={<CameraAvatar />}
            className={classes.badgedAvatar}
            onClick={uploadAvatar}
          >
            <Avatar
              className={classes.userAvatar}
              src={avatarProfile || avatar}
            />
          </Badge>
          <Box
            component="div"
            sx={{
              display: "flex",
              flexdirection: "row",
              height: "100%",
              justifyContent: "flex-end",
              alignItems: "flex-end",
            }}
          >
          </Box>
        </Box>
        <Box flex={1} style={{ minHeight: 100 }}>
          <Grid container alignItems="center" className={classes.mainContent}>
            <div className={classes.viewAs}>
              <Tooltip
                arrow
                placement="left"
                title={<FormattedMessage id="tooltip.preview" />}
              >
                <Button
                  variant="contained"
                  color="primary"
                  className={classes.button}
                  size="small"
                  onClick={() => setIsViewedAs(true)}
                >
                  <VisibilityIcon />
                </Button>
              </Tooltip>
            </div>
            <Grid item sm={12} md={6} style={{ flexGrow: 1 }}>
              <NameRow
                accountSettings={accountSettings}
                setAccountSettings={setAccountSettings}
              />
              <EmploymentRow
                accountSettings={accountSettings}
                setAccountSettings={setAccountSettings}
              />
              <AddressRow
                accountSettings={accountSettings}
                setAccountSettings={setAccountSettings}
              />
              <BioDescription
                accountSettings={accountSettings}
                setAccountSettings={setAccountSettings}
              />
            </Grid>
          </Grid>
        </Box>
      </Box>
      {openUpload && (
        <AvatarUpload
          defaultAvatar={Profile}
          gravatarUrl={gravatarUrl}
          setAvatarProfile={setAvatarProfile}
          setOpenUpload={setOpenUpload}
        />
      )}
    </>
  );
};

BannerSection.propTypes = {
  accountSettings: PropTypes.shape({}),
  setAccountSettings: PropTypes.func.isRequired,
  setIsViewedAs: PropTypes.func.isRequired,
};

export default BannerSection;
